import React, { useContext, useEffect, useRef, useState } from "react";
import ICart_Item from "@models/ICart_Item";
import { OwIconButton } from "@components/shared/OwIconButton/OwIconButton";
import OwS3ImageViewer from "@components/shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "@models/IImageSizeEnum";
import { EcommerceCartItemQty } from "../EcommerceCartItemQty/EcommerceCartItemQty";
import IPrice_Group from "@models/IPrice_Group";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OrderEntryService from "@services/OrderEntryService";
import { UomSetSelectOption } from "@components/shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "@models/IUnit_Of_Measurement_Set";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import VendorContext from "@contexts/VendorContext";
import UserService from "@services/UserService";
import { useCart } from "@contexts/CartContext";
import IOrder from "@models/IOrder";
import HelperService from "@services/HelperService";
import "./EcommerceCartFnItem.scss";
import { startCase } from "lodash-es";
import ICatalog from "@models/ICatalog";
import CatalogService from "@services/CatalogService";
import { getPackItemTotalUnits } from "@services/CatalogItemService";

type Props = {
    cartItem: ICart_Item;
    updateQty: (qty: number, set?: IUnit_Of_Measurement_Set) => void;
    deleteItem: (item: ICart_Item) => void;
    catalogModel?: ICatalog;
    priceGroup?: IPrice_Group;
    kitBuilderItems?: ICart_Item[];
    /** Re-open the kit builder for this kit line (kit builder items only). */
    editKit?: (item: ICart_Item) => void;
};

export const EcommerceCartFnItem: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const { cart } = useCart();

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    let currencyName = props.catalogModel?.Currency?.name ? props.catalogModel.Currency.name : "USD";

    if (props.catalogModel?.currency_id) {
        currencyName = CatalogService.getCurrencyNameFromId(props.catalogModel.currency_id);
    }

    let priceEach = props.priceGroup
        ? OrderEntryService.getDiscountedPriceForCartItem(props.cartItem, props.priceGroup, supplierModel, cart?.supplierVendorToken)
        : 0;

    const getUom = () => {
        if (props.cartItem.Selected_Unit) {
            return props.cartItem.Selected_Unit?.unit_abbr || "";
        } else {
            return props.cartItem.Catalog_Item?.Unit_Of_Measurement?.abbreviation || "";
        }
    };

    const uom = getUom().toLowerCase();
    const [selectedUnit, setSelectedUnit] = useState<IUnit_Of_Measurement_Set | undefined>(props.cartItem.Selected_Unit);
    const hasInitialRender = useRef(false);

    useEffect(() => {
        if (selectedUnit && hasInitialRender.current) {
            const qtyDetail = OrderEntryService.doInventoryValidation(
                props.cartItem.Catalog_Item!,
                props.cartItem.qty,
                [],
                supplierModel,
                {} as IOrder,
                0,
                selectedUnit
            );

            updateQty(qtyDetail.qty);
        }
        hasInitialRender.current = true;
    }, [selectedUnit]);

    let totalPrice = 0;

    if (props.cartItem.Selected_Unit) {
        priceEach = priceEach * (props.cartItem.Selected_Unit?.conversion_factor || 1);
    }

    if (props.cartItem.qty) {
        totalPrice = props.cartItem.qty * priceEach;
    }

    const selectedOptionFn = (option: IUnit_Of_Measurement_Set) => {
        setSelectedUnit(option);
    };

    const updateQty = (qty: number) => {
        const qtyDetail = OrderEntryService.doInventoryValidation(
            props.cartItem.Catalog_Item!,
            qty,
            [],
            supplierModel,
            {} as IOrder,
            0,
            selectedUnit
        );

        props.updateQty(qtyDetail.qty, selectedUnit);
    };

    return (
        <div className={"EcommerceCartFnItem cartItem"}>
            <div className={"cartItemDetail"}>
                <div>
                    <OwS3ImageViewer
                        catalogItem={props.cartItem.Catalog_Item}
                        isMicroView={true}
                        attachmentIndex={0}
                        imageIndex={IImageSizeEnum.THUMB}
                        className={"w-5r h-5r"}
                        includeAllImages={true}
                        hideEmptyImage={false}
                    />
                </div>
                <div className={"cartItemName"}>
                    {props.cartItem.parent_cart_item_id ? (
                        <div className={"text-small text-muted"}>
                            <OwLabel title={"kit_item"} />
                        </div>
                    ) : null}
                    <div>{props.cartItem.Catalog_Item?.name}</div>
                    <div className={"text-small"}>{props.cartItem.Catalog_Item?.sku}</div>
                    {props.cartItem.Cart_Item_Options && (
                        <div className={"mt-2 mb-2"}>
                            {props.cartItem.Cart_Item_Options?.map((itemOption) => {
                                return (
                                    <div key={itemOption.item_option_id}>
                                        <b>{itemOption.Item_Option?.name}:</b> {startCase(itemOption.value)}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {props.cartItem.parent_cart_item_id || hidePricing ? null : (
                        <div className={"mt-2"}>
                            <div>
                                {HelperService.formatCurrency({
                                    value: priceEach,
                                    currency: currencyName,
                                    precision: supplierModel.pricing_precision || 2,
                                })}{" "}
                                {uom.length ? <span>/ {uom}</span> : null}
                            </div>
                        </div>
                    )}
                    <div>
                        {props.cartItem.Catalog_Item?.has_pack_qty ? (
                            <div className={"text-small"}>
                                <OwLabel title={"pack_qty"} postText={":"} /> {props.cartItem.Catalog_Item?.pack_qty}
                            </div>
                        ) : null}
                    </div>
                    <div>
                        {props.cartItem.Catalog_Item?.has_pack_qty ? (
                            <div>
                                <OwLabel title={"total_qty"} postText={":"} className={"me-2"} />
                                {getPackItemTotalUnits(props.cartItem.Catalog_Item, props.cartItem.qty)}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className={"cartOptions"}>
                <EcommerceCartItemQty
                    cartItem={props.cartItem}
                    updateFn={updateQty}
                    readOnly={!!props.cartItem.parent_cart_item_id}
                    uomSet={selectedUnit}
                />
                {props.cartItem.Selected_Unit ? (
                    <UomSetSelectOption
                        item={props.cartItem.Catalog_Item!}
                        selectedOption={selectedOptionFn}
                        currentOption={props.cartItem.Selected_Unit}
                    />
                ) : null}
                {props.cartItem.parent_cart_item_id ? null : (
                    <div className={"d-flex align-items-center ms-auto"}>
                        {props.cartItem.is_kit_builder && props.editKit ? (
                            <OwIconButton
                                iconClass={"fad fa-pencil"}
                                iconColorClass={"text-primary me-2"}
                                fn={() => {
                                    props.editKit!(props.cartItem);
                                }}
                            />
                        ) : null}
                        <OwIconButton
                            iconClass={"fad fa-trash-alt"}
                            iconColorClass={"text-danger"}
                            fn={() => {
                                props.deleteItem(props.cartItem);
                            }}
                        />
                    </div>
                )}
            </div>
            {props.kitBuilderItems?.length ? (
                <div className={"mt-2"}>
                    {
                        // filter out the sub items...
                        props.kitBuilderItems
                            .filter((kitItem) => kitItem.parent_cart_item_id === props.cartItem.id)
                            .map((item, index) => {
                                const subItems = props.kitBuilderItems!.filter((subItem) => subItem.parent_cart_item_id === item.id);

                                const mainItemDescription = item.Catalog_Item?.description ? (
                                    <span title={item.Catalog_Item?.description}> - {item.Catalog_Item?.description}</span>
                                ) : (
                                    ""
                                );

                                return (
                                    <div key={item.guid} className={"mb-2 border rounded"}>
                                        <div className={"bg-dark p-1 rounded-top"}>{item?.Kit_Builder_Option?.name}</div>
                                        <div className={"p-1 kit-item-row"}>
                                            {item.Catalog_Item?.name}
                                            {mainItemDescription}
                                        </div>
                                        {subItems?.map((subItem) => {
                                            const subItemDescription = subItem.Catalog_Item?.description
                                                ? ` - ${subItem.Catalog_Item?.description}`
                                                : "";
                                            return (
                                                <div key={subItem.guid} className={"ms-4"}>
                                                    <i className={"fas fa-turn-down-right me-1"} />
                                                    {subItem.Catalog_Item?.name}
                                                    {subItemDescription}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })
                    }
                </div>
            ) : null}
        </div>
    );
};
